import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { showDeleteConfirm } from '../utils/sweetAlert';
import {
  fetchQuestions,
  createQuestion,
  updateQuestion,
  deleteQuestion,
  fetchIndustries,
  fetchSubIndustries
} from '../utils/adminApiService';
import logger from '../utils/logger';
import { useDebounce } from '../hooks/useDebounce';
import AdminQuestionsHeader from './AdminQuestions/AdminQuestionsHeader';
import AdminQuestionsToolbar from './AdminQuestions/AdminQuestionsToolbar';
import AdminQuestionsTable from './AdminQuestions/AdminQuestionsTable';
import AdminQuestionsPagination from './AdminQuestions/AdminQuestionsPagination';
import AdminQuestionsAddModal from './AdminQuestions/AdminQuestionsAddModal';
import AdminQuestionsEditModal from './AdminQuestions/AdminQuestionsEditModal';

const AdminQuestions = () => {
  const { user, hasRole, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterIndustry, setFilterIndustry] = useState('all');
  const [filterAssessmentType, setFilterAssessmentType] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [subIndustries, setSubIndustries] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, limit: 20, offset: 0 });
  const itemsPerPage = 20;
  
  const [formData, setFormData] = useState({
    question_text: '',
    question_type: 'numeric',
    question_code: '',
    assessment_type: 'ESA',
    category: '',
    subcategory: '',
    industry_id: '',
    sub_industry_id: '',
    unit: '',
    required: true,
    options: '',
    related_arc_codes: '',
    potential_savings: '',
    payback_period: '',
    implementation_cost: '',
    confidence_level: ''
  });

  // Wait for auth to load before checking access
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-64 mb-4" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-48 mb-6" />
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-12 bg-gray-200 dark:bg-gray-700 rounded" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Access control
  if (!user || !hasRole('admin')) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Access Denied</h1>
          <p className="text-gray-600 dark:text-gray-400">You need admin privileges to access this page.</p>
          <button 
            onClick={() => navigate('/admin')} 
            className="mt-4 px-4 py-2 bg-emerald-600 dark:bg-emerald-700 text-white rounded-md hover:bg-emerald-700 dark:hover:bg-emerald-600"
          >
            Back to Admin Dashboard
          </button>
        </div>
      </div>
    );
  }

  const [initialLoad, setInitialLoad] = useState(true);

  // Initial load - only on mount or when auth changes
  useEffect(() => {
    if (authLoading) return;
    if (!user || !hasRole('admin')) return;
    loadQuestions(true);
    setInitialLoad(false);
  }, [authLoading, user]);

  // Reload when filters/search/page change (but don't show loading spinner after initial load)
  // Use debounced search to avoid triggering on every keystroke
  useEffect(() => {
    if (authLoading || initialLoad) return; // Skip if still on initial load
    if (!user || !hasRole('admin')) return;
    loadQuestions(false); // Don't show loading for filter changes
  }, [activeTab, filterCategory, filterIndustry, filterAssessmentType, currentPage, debouncedSearchQuery]);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !hasRole('admin')) return;
    loadIndustries();
  }, [authLoading, user]);

  const loadQuestions = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      const filters = {
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage
      };

      if (filterIndustry !== 'all') filters.industry_id = filterIndustry;
      if (filterAssessmentType !== 'all') filters.assessment_type = filterAssessmentType;
      if (filterCategory !== 'all') filters.category = filterCategory;
      if (debouncedSearchQuery) filters.search = debouncedSearchQuery;

      const data = await fetchQuestions(filters);
      
      if (data.success) {
        setQuestions(data.questions || []);
        setPagination(data.pagination || { total: 0, limit: itemsPerPage, offset: 0 });
      }
    } catch (error) {
      if (error.message === 'UNAUTHORIZED') {
        navigate('/login');
      } else {
        logger.error('Error loading questions:', error);
      }
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const loadIndustries = async () => {
    try {
      const data = await fetchIndustries(false);
      if (data.success) {
        setIndustries(data.industries || []);
      } else if (data.error) {
        // Only log non-timeout errors
        if (!data.error.includes('timeout') && !data.error.includes('ERR_CONTENT_LENGTH_MISMATCH')) {
          logger.error('Error loading industries:', data.error);
        } else {
          logger.debug('Industries load timeout/mismatch - will retry on next action');
        }
      }
    } catch (error) {
      // Handle network errors gracefully
      if (error.isNetworkError && (error.isTimeout || error.isContentLengthMismatch)) {
        logger.debug('Industries load timeout/mismatch - will retry on next action');
      } else {
        logger.error('Error loading industries:', error);
      }
    }
  };

  const loadSubIndustriesForIndustry = async (industryId) => {
    if (!industryId) {
      setSubIndustries([]);
      return;
    }
    
    try {
      // Find the industry to get its name and region
      const industry = industries.find(ind => ind.id === industryId);
      if (!industry) {
        setSubIndustries([]);
        return;
      }
      
      // Use the new endpoint with major_industry name and region
      const data = await fetchSubIndustries(null, industry.name, industry.region || 'naics');
      if (data.success) {
        setSubIndustries(data.sub_industries || []);
      } else {
        // Fallback to old endpoint with industry_id
        const fallbackData = await fetchSubIndustries(industryId);
        if (fallbackData.success) {
          setSubIndustries(fallbackData.sub_industries || []);
        } else {
          setSubIndustries([]);
        }
      }
    } catch (error) {
      logger.error('Error loading sub-industries:', error);
      setSubIndustries([]);
    }
  };

  const resetForm = () => {
    setFormData({
      question_text: '',
      question_type: 'numeric',
      question_code: '',
      assessment_type: 'ESA',
      category: '',
      subcategory: '',
      industry_id: '',
      sub_industry_id: '',
      unit: '',
      required: true,
      options: '',
      related_arc_codes: '',
      potential_savings: '',
      payback_period: '',
      implementation_cost: '',
      confidence_level: ''
    });
    setSubIndustries([]);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'industry_id') {
      loadSubIndustriesForIndustry(value);
      setFormData(prev => ({
        ...prev,
        industry_id: value,
        sub_industry_id: ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleAddQuestion = async (e) => {
    e.preventDefault();
    
    if (!formData.question_text || !formData.question_code || !formData.industry_id || !formData.assessment_type || !formData.category) {
      toast.error('Please fill in all required fields (Question Text, Code, Industry, Assessment Type, Category)');
      return;
    }

    try {
      setSubmitting(true);
      const selectedIndustry = industries.find(ind => ind.id === formData.industry_id);
      const industryRegion = selectedIndustry?.region || 'naics';

      // Ensure category is not empty
      if (!formData.category || formData.category.trim() === '') {
        toast.error('Category is required and cannot be empty');
        return;
      }

      const payload = {
        question_text: formData.question_text,
        question_type: formData.question_type,
        question_code: formData.question_code,
        assessment_type: formData.assessment_type,
        category: formData.category.trim(), // Ensure category is not null or empty
        subcategory: formData.subcategory?.trim() || null,
        industry_id: formData.industry_id,
        sub_industry_id: formData.sub_industry_id || null,
        region: industryRegion,
        unit: formData.unit?.trim() || null,
        required: formData.required
      };

      if (formData.options) {
        payload.options = formData.options.split(',').map(opt => opt.trim()).filter(Boolean);
      }
      if (formData.related_arc_codes) {
        payload.related_arc_codes = formData.related_arc_codes.split(',').map(code => code.trim()).filter(Boolean);
      }
      if (formData.potential_savings) payload.potential_savings = parseFloat(formData.potential_savings);
      if (formData.payback_period) payload.payback_period = parseFloat(formData.payback_period);
      if (formData.implementation_cost) payload.implementation_cost = parseFloat(formData.implementation_cost);
      if (formData.confidence_level) payload.confidence_level = formData.confidence_level;

      const result = await createQuestion(payload);
      if (result.success) {
        setShowAddModal(false);
        resetForm();
        await loadQuestions();
      }
    } catch (error) {
      if (error.message === 'UNAUTHORIZED') {
        navigate('/login');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditClick = (question) => {
    setEditingQuestion(question);
    setFormData({
      question_text: question.question_text || '',
      question_type: question.question_type || 'numeric',
      question_code: question.question_code || '',
      assessment_type: question.assessment_type || 'ESA',
      category: question.category || '',
      subcategory: question.subcategory || '',
      industry_id: question.industry_id || '',
      sub_industry_id: question.sub_industry_id || '',
      industry_name: question.industry_name || '',
      sub_industry_name: question.sub_industry_name || '',
      question_region: question.region || '',
      unit: question.unit || '',
      required: question.required !== undefined ? question.required : true,
      options: Array.isArray(question.options) ? question.options.join(', ') : '',
      related_arc_codes: Array.isArray(question.related_arc_codes) ? question.related_arc_codes.join(', ') : '',
      potential_savings: question.potential_savings || '',
      payback_period: question.payback_period || '',
      implementation_cost: question.implementation_cost || '',
      confidence_level: question.confidence_level || ''
    });
    
    if (question.industry_id) {
      loadSubIndustriesForIndustry(question.industry_id);
    }
    
    setShowEditModal(true);
  };

  const handleUpdateQuestion = async (e) => {
    e.preventDefault();
    if (!editingQuestion) return;

    // Validate required fields
    if (!formData.category || formData.category.trim() === '') {
      toast.error('Category is required and cannot be empty');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        question_text: formData.question_text,
        question_type: formData.question_type,
        question_code: formData.question_code,
        category: formData.category.trim(), // Ensure category is not null or empty
        subcategory: formData.subcategory?.trim() || null,
        unit: formData.unit?.trim() || null,
        required: formData.required
      };

      if (formData.options) {
        payload.options = formData.options.split(',').map(opt => opt.trim()).filter(Boolean);
      }
      if (formData.related_arc_codes) {
        payload.related_arc_codes = formData.related_arc_codes.split(',').map(code => code.trim()).filter(Boolean);
      }
      if (formData.potential_savings) payload.potential_savings = parseFloat(formData.potential_savings);
      if (formData.payback_period) payload.payback_period = parseFloat(formData.payback_period);
      if (formData.implementation_cost) payload.implementation_cost = parseFloat(formData.implementation_cost);
      if (formData.confidence_level) payload.confidence_level = formData.confidence_level;

      const result = await updateQuestion(editingQuestion.id, payload);
      if (result.success) {
        setShowEditModal(false);
        setEditingQuestion(null);
        resetForm();
        await loadQuestions();
      }
    } catch (error) {
      if (error.message === 'UNAUTHORIZED') {
        navigate('/login');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    const confirmed = await showDeleteConfirm('this question');
    if (!confirmed) {
      return;
    }

    try {
      await deleteQuestion(questionId);
      loadQuestions();
    } catch (error) {
      if (error.message === 'UNAUTHORIZED') {
        navigate('/login');
      }
    }
  };

  const filteredQuestions = questions;
  const categories = ['all', ...new Set(questions.map(q => q.category).filter(Boolean))];

  const uniqueIndustries = React.useMemo(() => {
    const seen = new Set();
    return industries.filter(ind => {
      if (seen.has(ind.id)) return false;
      seen.add(ind.id);
      return true;
    }).map(ind => ({
      ...ind,
      displayName: `${ind.name} (${ind.region?.toUpperCase() || 'N/A'})`
    }));
  }, [industries]);

  const totalPages = Math.ceil((pagination.total || 0) / itemsPerPage);

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleAssessmentTypeChange = (value) => {
    setFilterAssessmentType(value);
    setCurrentPage(1);
  };

  const handleCategoryChange = (value) => {
    setFilterCategory(value);
    setCurrentPage(1);
  };

  const handleIndustryChange = (value) => {
    setFilterIndustry(value);
    setCurrentPage(1);
  };

  const handleOpenAddModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    resetForm();
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingQuestion(null);
    resetForm();
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      {/* Toaster removed - using global Toaster from App.js */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AdminQuestionsHeader
          loading={loading}
          total={pagination.total}
          onBack={() => navigate('/admin')}
        />

        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          <AdminQuestionsToolbar
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            filterAssessmentType={filterAssessmentType}
            onFilterAssessmentTypeChange={handleAssessmentTypeChange}
            filterCategory={filterCategory}
            onFilterCategoryChange={handleCategoryChange}
            categories={categories}
            filterIndustry={filterIndustry}
            onFilterIndustryChange={handleIndustryChange}
            uniqueIndustries={uniqueIndustries}
            onAddClick={handleOpenAddModal}
          />

          <AdminQuestionsTable
            loading={loading}
            questions={filteredQuestions}
            onEditClick={handleEditClick}
            onDeleteClick={handleDeleteQuestion}
          />

          <AdminQuestionsPagination
            pagination={pagination}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>

        <AdminQuestionsAddModal
          visible={showAddModal}
          formData={formData}
          uniqueIndustries={uniqueIndustries}
          subIndustries={subIndustries}
          submitting={submitting}
          onClose={handleCloseAddModal}
          onChange={handleFormChange}
          onSubmit={handleAddQuestion}
        />

        <AdminQuestionsEditModal
          visible={showEditModal}
          editingQuestion={editingQuestion}
          formData={formData}
          submitting={submitting}
          onClose={handleCloseEditModal}
          onChange={handleFormChange}
          onSubmit={handleUpdateQuestion}
        />
      </div>
    </div>
  );
};

export default AdminQuestions;
